import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';

const DetailField = ({ label, value }) => (
  <Grid item xs={12} md={6}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body1">{value ?? '-'}</Typography>
  </Grid>
);

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DetailField.defaultProps = {
  value: null,
};

const RequestDetails = () => {
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  const reqIdQuery = searchParams.get('reqId');
  const effectiveRequestId = requestId || reqIdQuery;
  const { loading, error, data, execute } = useAPICall();
  const {
    loading: accountLoading,
    error: accountError,
    data: accountData,
    execute: executeAccount,
  } = useAPICall();

  const request = useMemo(() => data?.request || data?.data || data || {}, [data]);
  const account = useMemo(() => accountData?.data || accountData || {}, [accountData]);

  useEffect(() => {
    const load = async () => {
      try {
        const endpointConfig = reqIdQuery ? API_CONFIG.workflow.getClosedDcrByRequestId : API_CONFIG.inbox.getRequestDetails;
        const endpointParams = reqIdQuery ? { reqId: effectiveRequestId } : { requestId: effectiveRequestId };

        const response = await execute(endpointConfig, null, {
          params: endpointParams,
          errorMessage: 'Unable to load request details.',
          retries: 1,
        });

        const requestPayload = response?.request || response?.data || response || {};
        const mdmId = requestPayload.mdmId || requestPayload.mdm_id;
        const accountType = requestPayload.mdmAccountType || requestPayload.entity_type || requestPayload.entity;

        if (mdmId && accountType) {
          const accountConfig = String(accountType).toLowerCase().includes('hco')
            ? API_CONFIG.workflow.getHcoByMdmId
            : API_CONFIG.workflow.getHcpByMdmId;

          await executeAccount(accountConfig, null, {
            params: { mdmId },
            showErrorToast: false,
            errorMessage: 'Unable to load account details for this request.',
          });
        }
      } catch {
      }
    };
    load();
  }, [effectiveRequestId, execute, executeAccount, reqIdQuery]);

  const handleAction = async (apiConfig, successMessage) => {
    try {
      await execute(apiConfig, {}, {
        params: { requestId: effectiveRequestId },
        successMessage,
        showSuccessToast: true,
        errorMessage: 'Unable to update request status.',
      });
    } catch {
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <PageBreadcrumbs items={[
          { label: 'Inbox', path: '/inbox/hcp' },
          { label: 'Request Details' }
        ]} />
      </Box>
      <Typography sx={{ fontWeight: 600,  color: '#1e293b', mb: 0.25 }}>Request Details</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Review request payload and take action
      </Typography>

      {loading ? (
        <Paper sx={{ p: 2, mb: 1.5 }}>
          <Grid container spacing={2}>
            {Array.from({ length: 7 }).map((_, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="80%" height={24} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      ) : (
        <Paper sx={{ p: 2, mb: 1.5 }}>
          <Grid container spacing={2}>
            <DetailField label="Request ID" value={request.request_id || request.requestId || request.id || effectiveRequestId} />
            <DetailField label="Entity Type" value={request.entity_type || request.entity || request.mdmAccountType} />
            <DetailField label="Action" value={request.action || request.request_type} />
            <DetailField label="Status" value={request.status} />
            <DetailField label="Submitted By" value={request.submitted_by || request.created_by} />
            <DetailField label="Created At" value={request.created_at || request.createdAt} />
            <DetailField label="Comments" value={request.comments || request.notes} />
          </Grid>

          {(accountLoading || accountError || Object.keys(account).length > 0) && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Account Details</Typography>
              {accountError && <Alert severity="warning" sx={{ mb: 1 }}>{accountError}</Alert>}
              {accountLoading ? (
                <Grid container spacing={2}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Grid item xs={12} md={6} key={i}>
                      <Skeleton variant="text" width="60%" height={24} />
                      <Skeleton variant="text" width="80%" height={24} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <DetailField label="MDM ID" value={account.mdmId || account.mdm_id} />
                  <DetailField label="Name" value={account.name || `${account.firstName || ''} ${account.lastName || ''}`.trim()} />
                  <DetailField label="NPI / Org ID" value={account.npiId || account.npi || account.orgId || account.org_id} />
                  <DetailField label="Source" value={account.source} />
                </Grid>
              )}
            </Box>
          )}
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="success"
          onClick={() => handleAction(API_CONFIG.inbox.approveRequest, 'Request approved')}
          disabled={loading}
        >
          Approve
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => handleAction(API_CONFIG.inbox.rejectRequest, 'Request rejected')}
          disabled={loading}
        >
          Reject
        </Button>
      </Box>
    </Box>
  );
};

export default RequestDetails;
